/**
 * Why an enhanced model is not on screen — said out loud, exactly once.
 *
 * ## The failure this module exists for
 *
 * `data/enhanced` is optional (`docs/engineering/enhanced-models.md`), so every
 * step of the enhanced path is written to fall back to the baked aeroplane
 * rather than fail: a missing manifest is an empty index, a `.glb` that will not
 * load is `undefined`, a model whose paint cannot be moved is refused. That is
 * the right *behaviour* and it stays. What it cost is the *diagnosis*: a
 * deployment that has the models and cannot serve them looks pixel-for-pixel
 * identical to one that never built them, and ENHANCED MODELS reads ON while
 * changing nothing at all.
 *
 * It happened. A static host whose SPA catch-all answers every unmatched path
 * with `index.html` and a **200** — which is what a catch-all is for — served
 * `/enhanced/manifest.json` as HTML. `fetchEnhancedIndex` checked `response.ok`,
 * got `true`, failed to parse the HTML as JSON, and returned the same empty
 * index it returns for "the tool was never run". Every airframe in every screen
 * silently drew its baked `.TBL`, in both toggle states, with nothing in the
 * console. The bug report was "I am not seeing a single difference between
 * enhanced models and default models in either game or plane preview screen"
 * (user, 2026-08-23), and there was no way to tell from inside the app that the
 * models were sitting on disk one route away.
 *
 * So: fall back silently in *pixels*, never in the *console*. A warning here
 * costs nothing on the install that legitimately has no enhanced models — that
 * case is `absent`, and it says so once and calmly — and on the broken one it
 * names the route and the reason on the first aeroplane loaded.
 *
 * Once per distinct message, because these run per aeroplane per sortie and a
 * fleet of eight would otherwise print eight copies of the same line.
 */

const said = new Set<string>();

/** Prints `message` the first time it is seen, and never again for the life of the document. */
export function warnEnhancedOnce(message: string): void {
  if (said.has(message)) return;
  said.add(message);
  console.warn(`[enhanced models] ${message}`);
}

/** Test seam: forget what has been said, so one test's warning does not silence the next one's. */
export function resetEnhancedWarnings(): void {
  said.clear();
}
